import { scheduleResource } from '@onward-delivery/core';

// "Working that day" and "taking new bookings that day" are two different facts, and
// scheduleResource() merges them: it tests overrides first and returns !omit, so an omit override
// short-circuits the rules and the resource reads as though it does not exist that day. That is
// right for the Plan page — a closed resource must not offer a shell route — but wrong for the
// Resources calendar, which has to show it, muted, with the toggle still reachable.
//
// `scheduled` reuses core with omit overrides stripped, so rule semantics (WEEKDAYS, WEEKLY,
// BIWEEKLY, MONTHLY) stay in one place. Only the override window is matched here.
//
// `entity` is the recurrence row; `blockingOverride` is the closure. Merging them made every closed
// day report itself as a one-off, so Recurring read "Does not repeat" and the scope prompt vanished.

// core's overlaps(), which it does not export. The bounds have to be read exactly as core reads
// them or the calendar and the DB disagree about which day an override belongs to.
const covers = (start, end, day) => {
    const from = new Date(start).getTime();
    const to = end ? new Date(end).getTime() : null;
    const dayStart = new Date(day.start).getTime();
    const dayEnd = new Date(day.end).getTime();

    return from <= dayStart && (to === null || to === dayEnd || to > dayStart);
};

export const resourceDayState = (resource, day) => {
    if (!resource || !day?.start || !day?.end) {
        return {
            scheduled: false,
            accepting: true,
            coveredByRule: false,
            entity: null,
            ruleEntity: null,
            blockingOverride: null,
        };
    }

    const rules = resource.rules || [];
    const overrides = resource.overrides || [];
    const blocking = overrides.find((o) => o.omit && covers(o.override_start, o.override_end, day));

    const [scheduled, entity] = scheduleResource({
        resource: { ...resource, rules, overrides: overrides.filter((o) => !o.omit) },
        day,
    });

    // Decides what reopening does with the closure: drop it when a rule already puts the resource
    // here, or flip it to omit:false when it is a "Does not repeat" resource's only row.
    //
    // The probe keeps its entity as well as its boolean. `entity` is whichever row scheduleResource
    // matched first, which is an override whenever one covers the day, so it cannot name the rule a
    // new closure excepts — that is what `ruleEntity` is for. With overrides stripped the probe can
    // only ever return a rules entity or null. Short-circuited on the rule case so a rule-covered
    // day still costs one scheduleResource call, as before.
    const ruleProbe =
        entity?.type === 'rules'
            ? [true, entity]
            : scheduleResource({ resource: { ...resource, rules, overrides: [] }, day });

    return {
        scheduled,
        accepting: !blocking,
        coveredByRule: ruleProbe[0],
        entity,
        ruleEntity: ruleProbe[1],
        blockingOverride: blocking || null,
    };
};
